import React, { useMemo, useState } from "react";
import { UserCheck, GripVertical, CalendarClock } from "lucide-react";
import { initials, fmtDate } from "./timeline";

export function AvailablePanel({ items, draggable = false, onDragStart, onDragEnd, className = "" }) {
  return (
    <div className={`surface p-4 ${className}`} data-testid="available-panel">
      <div className="flex items-center gap-2 mb-2">
        <UserCheck className="h-4 w-4 text-emerald-600" />
        <div className="font-semibold text-[13px] text-slate-800">Resurse disponibile</div>
        <span className="pio-badge pio-badge-green ml-auto" data-testid="available-count">{items.length}</span>
      </div>
      <p className="text-[11px] text-slate-400 mb-3">
        Angajați și colaboratori activi fără nicio alocare curentă sau viitoare pe proiecte.
        {draggable && " Trage-i pe un proiect pentru a-i aloca."}
      </p>
      {items.length === 0 ? (
        <div className="text-[12px] text-slate-400 py-4 text-center">Toate resursele sunt alocate.</div>
      ) : (
        <div className="space-y-1.5 max-h-[60vh] overflow-y-auto pr-1">
          {items.map((e) => (
            <div key={e.id}
              draggable={draggable}
              onDragStart={draggable ? (ev) => onDragStart(ev, e) : undefined}
              onDragEnd={onDragEnd}
              data-testid={`available-emp-${e.id}`}
              className={`flex items-center gap-2.5 rounded-lg border border-emerald-100 bg-emerald-50/50 px-2.5 py-2 ${draggable ? "cursor-grab active:cursor-grabbing hover:border-emerald-300 transition-colors" : ""}`}>
              {draggable && <GripVertical className="h-3.5 w-3.5 text-slate-300 shrink-0" />}
              <span className="h-7 w-7 rounded-full bg-white text-emerald-700 border border-emerald-200 flex items-center justify-center text-[10px] font-semibold shrink-0">
                {initials(e.name)}
              </span>
              <div className="min-w-0">
                <div className="text-[12.5px] font-medium text-slate-800 leading-tight truncate">{e.name}</div>
                <div className="text-[11px] text-slate-400 leading-tight truncate">
                  {e.job_title || e.role}
                  <span className="text-emerald-600 font-medium"> · {e.free_pct != null ? e.free_pct : 100}% liber</span>
                </div>
                {e.freed_from && (
                  <div className="text-[10.5px] text-amber-700 leading-tight truncate">
                    eliberat de pe {e.freed_from.project_code || e.freed_from.project_name} la {fmtDate(e.freed_from.end_date)}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function AvailableFuturePanel({ items = [], today, horizon: controlledHorizon, className = "" }) {
  const [internal, setInternal] = useState(3); // luni
  const horizon = controlledHorizon != null ? controlledHorizon : internal;

  const limit = useMemo(() => {
    const base = today ? new Date(today + "T00:00:00") : new Date();
    const d = new Date(base);
    d.setMonth(d.getMonth() + horizon);
    return d;
  }, [today, horizon]);

  const filtered = useMemo(() => {
    return (items || []).filter((e) => {
      if (!e.free_from) return false;
      const f = new Date(e.free_from + "T00:00:00");
      return !isNaN(f) && f <= limit;
    });
  }, [items, limit]);

  return (
    <div className={`surface p-4 ${className}`} data-testid="available-future-panel">
      <div className="flex items-center gap-2 mb-2">
        <CalendarClock className="h-4 w-4 text-brand" />
        <div className="font-semibold text-[13px] text-slate-800">Resurse disponibile în viitor</div>
        <span className="pio-badge pio-badge-blue ml-auto" data-testid="available-future-count">{filtered.length}</span>
      </div>
      <p className="text-[11px] text-slate-400 mb-3">
        Angajați ocupați acum, dar a căror alocare cu perioadă se termină în intervalul ales — devin disponibili la data indicată.
      </p>
      <div className="flex items-center gap-1 mb-3" data-testid="available-future-horizon">
        {(controlledHorizon != null ? [controlledHorizon] : [1, 2, 3, 4, 5, 6]).map((n) => (
          <button key={n} onClick={() => setInternal(n)} disabled={controlledHorizon != null}
            data-testid={`horizon-${n}`}
            className={`flex-1 py-1 rounded-md text-[11px] font-medium border transition-colors ${
              horizon === n ? "bg-[#3d68b1] text-white border-[#3d68b1]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
            } ${controlledHorizon != null ? "cursor-default" : ""}`}>
            {n} {n === 1 ? "lună" : "luni"}
          </button>
        ))}
      </div>
      {filtered.length === 0 ? (
        <div className="text-[12px] text-slate-400 py-4 text-center">
          Nicio resursă nu se eliberează în {horizon} {horizon === 1 ? "lună" : "luni"}.
        </div>
      ) : (
        <div className="space-y-1.5 max-h-[60vh] overflow-y-auto pr-1">
          {filtered.map((e) => (
            <div key={e.id} data-testid={`available-future-emp-${e.id}`}
              className="flex items-center gap-2.5 rounded-lg border border-blue-100 bg-blue-50/40 px-2.5 py-2">
              <span className="h-7 w-7 rounded-full bg-white text-brand border border-blue-200 flex items-center justify-center text-[10px] font-semibold shrink-0">
                {initials(e.name)}
              </span>
              <div className="min-w-0">
                <div className="text-[12.5px] font-medium text-slate-800 leading-tight truncate">{e.name}</div>
                <div className="text-[11px] text-slate-400 leading-tight truncate">{e.job_title || e.role}</div>
                <div className="text-[10.5px] text-brand leading-tight truncate">
                  disponibil din {fmtDate(e.free_from)}
                  {e.freeing_from && ` · de pe ${e.freeing_from.project_code || e.freeing_from.project_name}`}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
